import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Alert } from '@mui/material';
import { listAthleteTeamMemberships } from '../../services/rosterService';
import { groupMembershipsBySeason, formatRosterLine } from '../../utils/footballHistory';

/**
 * Phase C.6 — ATHLETE IDENTITY & ROSTER HISTORY FOUNDATION. The minimum
 * read experience needed to prove the architecture test: "can FFN
 * reconstruct who this athlete played for, what grade they were in,
 * what number they wore, and what positions they played during any
 * historical season?" This is NOT the polished Football Card (no photo,
 * no Data Web, no Nerd Rating — all explicitly later phases) — it's a
 * plain, season-grouped list built entirely from data that already
 * exists (team_athletes' own season_grade/jersey_number/primary_
 * position/secondary_position, 047, plus the existing Team -> Division
 * -> Season chain) — never a duplicated display string stored anywhere.
 *
 * Deliberately separate from TeamMembershipSection (same page) — that
 * component's job is CURRENT status + pending-request management
 * (Cancel a request, "Join a Team"); this one's job is read-only
 * historical reconstruction, season by season, including memberships
 * TeamMembershipSection already shows in its own "Previous Membership
 * Period(s)" bucket. Showing the same underlying rows through two
 * different lenses is intentional, not duplication of data.
 *
 * Readable under EXISTING RLS with zero broadened access: this is the
 * athlete's OWN guardian viewing their OWN athlete's full history
 * (already fully permitted — "guardians view their athletes team
 * memberships" territory, 012), and divisions/seasons are already
 * readable by any authenticated user (036) — no new RPC, no new policy.
 */
export default function FootballHistorySection({ athlete }) {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const m = await listAthleteTeamMemberships(athlete.id);
    setMemberships(m);
  }, [athlete.id]);

  useEffect(() => {
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  if (loading) return null; // avoid a second spinner competing with the page's own
  if (error) return <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>;
  // Nothing to reconstruct yet — TeamMembershipSection already covers
  // the "not connected to any team yet" empty state; no need to repeat it.
  if (memberships.length === 0) return null;

  const groups = groupMembershipsBySeason(memberships);

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mt: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Football History</Typography>
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 2 }}>
          {athlete.first_name}'s football history by season.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groups.map(group => (
            <Box key={group.year ?? 'unknown'}>
              <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em', fontWeight: 700, mb: 0.75 }}>
                {group.year !== null ? group.year : 'SEASON UNKNOWN'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {group.memberships.map(m => (
                  <Box key={m.id} sx={{ opacity: m.status === 'active' ? 1 : 0.7 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {m.team?.team_name || 'Team'}
                      {m.status !== 'active' && (
                        <Typography component="span" sx={{ color: '#6a8aa8', fontWeight: 500, fontSize: '0.75rem' }}> · inactive</Typography>
                      )}
                    </Typography>
                    {/* BROWSER-FOUND FIX (C.6 follow-up) — a standalone
                        Division-name line used to render here too (for
                        disambiguation, per this phase's original design).
                        In practice a Division's own name is very often
                        itself grade-shaped ("6th Grade"), which read as
                        the athlete's grade being shown twice in a row —
                        once as a bare Division-name line, once inside
                        formatRosterLine()'s own "6th Grade · #21 · RB /
                        LB". Removed rather than special-cased: one line
                        per membership, exactly the grade/jersey/position
                        facts this section exists to prove, never a
                        second line that may or may not restate one of
                        them depending on how a Program happened to name
                        its Division. divisionLabel() itself is untouched
                        (utils/footballHistory.js) — still available if a
                        future pass needs real Team-name disambiguation. */}
                    <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', mt: 0.25 }}>{formatRosterLine(m)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
